import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';

// Universal search box for the sidebar. Debounced live results from GET /api/search,
// grouped by type (jobs, clients, contacts, quotes, invoices, notes), click to navigate.
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setGroups([]); setLoading(false); return undefined; }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await axios.get(`${API_URL}/search?q=${encodeURIComponent(q.trim())}`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setGroups(res.data.groups || []);
        setOpen(true);
      } catch (e) {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const go = (link) => {
    setOpen(false);
    setQ('');
    setGroups([]);
    navigate(link);
  };

  // Enter / "See all" → open the full results page.
  const seeAll = () => {
    const term = q.trim();
    if (term.length < 2) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const totalCount = groups.reduce((n, g) => n + (g.items ? g.items.length : 0), 0);

  return (
    <div ref={boxRef} style={{ padding: '0 20px 10px', position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <FiSearch size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.6)', pointerEvents: 'none' }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (groups.length) setOpen(true); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); seeAll(); } }}
          placeholder="Search everything…"
          style={{
            width: '100%', padding: '9px 30px 9px 32px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.1)',
            color: 'white', fontSize: 14, outline: 'none', boxSizing: 'border-box',
          }}
        />
        {q && (
          <button onClick={() => { setQ(''); setGroups([]); setOpen(false); }}
            title="Clear"
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <FiX size={15} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div style={{
          position: 'absolute', left: 20, right: 20, top: '100%', zIndex: 99990,
          background: 'white', color: '#1e293b', borderRadius: 10, marginTop: 4,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)', maxHeight: '70vh', overflowY: 'auto',
        }}>
          {loading && totalCount === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: '#64748b' }}>Searching…</div>
          ) : totalCount === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: '#64748b' }}>No results for &ldquo;{q}&rdquo;.</div>
          ) : (
            <>
              {groups.map((g) => (
                <div key={g.type}>
                  <div style={{ padding: '8px 12px 4px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8' }}>{g.label}</div>
                  {g.items.map((it, i) => (
                    <button key={g.type + i} onClick={() => go(it.link)} onMouseDown={(e) => e.preventDefault()}
                      style={{ display: 'flex', justifyContent: 'space-between', gap: 8, width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', borderTop: '1px solid #f1f5f9', cursor: 'pointer', fontSize: 13 }}
                    >
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>{it.title}</span>
                        {it.subtitle ? <span style={{ color: '#64748b' }}> — {it.subtitle}</span> : null}
                      </span>
                      {it.meta ? <span style={{ color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>{it.meta}</span> : null}
                    </button>
                  ))}
                </div>
              ))}
              <button onMouseDown={(e) => e.preventDefault()} onClick={seeAll}
                style={{ width: '100%', textAlign: 'center', padding: '10px 12px', background: '#f8fafc', border: 'none', borderTop: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#2563eb' }}>
                See all results on one page →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
